import React, { useEffect, useState } from "react";
import Modal from "./ui/Modal";
import Button from "./ui/Button";
import { IconChevronDown, IconChevronLeft, IconChevronRight } from "./ui/icons";
import { ListSkeleton } from "./ui/Skeleton";
import { EmptyState, ErrorState } from "./ui/StateBlock";
import LoadingOverlay from "./ui/LoadingOverlay";
import { formatNumber } from "./charts/chartTheme";
import { useApiData } from "../hooks/useApiData";
import { unwrapArray } from "../utils/api";

const PAGE_SIZE = 25;

// "2026-10-06 14:23:11" -> "06.10.2026 14:23"
export function formatDateTime(value) {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : String(value || "");
}

/**
 * Modal with a paginated list of records (receipts, expenses).
 * endpoint: PHP script, query: base parameters (od, do, warehouse), listKey: key of the array in the response,
 * getId: what is shown as the record number/ID, noun: "računa" / "rashoda" for the summary row (UI text).
 */
export default function RecordListModal({ open, onClose, title, ...rest }) {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <RecordList {...rest} />
    </Modal>
  );
}

function RecordList({ endpoint, query, listKey, getId, idLabel, noun, emptyTitle, detail }) {
  const [page, setPage] = useState(1);
  const [retryTick, setRetryTick] = useState(0);

  // New period/warehouse: back to the first page.
  useEffect(() => {
    setPage(1);
  }, [query]);

  // The totals (count, sum, number of pages) are the expensive part on the server, so they are requested only with the
  // first page; later pages send skipTotal=1 and reuse the remembered totals.
  const [totals, setTotals] = useState(null);
  const canSkipTotal = page > 1 && totals && totals.query === query;
  const { data, loading, error } = useApiData(
    `${endpoint}?${query}&page=${page}&pageSize=${PAGE_SIZE}${canSkipTotal ? "&skipTotal=1" : ""}`,
    retryTick
  );
  useEffect(() => {
    if (data?.success && data.ukupno !== null && data.ukupno !== undefined) {
      setTotals({ query, ukupno: data.ukupno, zbir: data.zbir, pages: data.strana_ukupno || 1 });
    }
  }, [data, query]);
  const failed = Boolean(error) || (data && data.success === false);
  const rows = data?.success ? unwrapArray(data[listKey]) : [];
  const known = data?.success && data.ukupno !== null && data.ukupno !== undefined ? { ukupno: data.ukupno, zbir: data.zbir, pages: data.strana_ukupno || 1 } : totals && totals.query === query ? totals : null;
  const pages = known ? known.pages : 1;

  if (failed && rows.length === 0) {
    return <ErrorState message={data?.error} onRetry={() => setRetryTick((t) => t + 1)} />;
  }
  if (loading && rows.length === 0) {
    return <ListSkeleton rows={8} />;
  }
  if (!loading && data?.success && known?.ukupno === 0) {
    return <EmptyState title={emptyTitle} hint="Probaj drugi period ili magacin." />;
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-50">{formatNumber(known?.ukupno)}</span> {noun}
        <span aria-hidden="true"> · </span>
        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-50">{formatNumber(known?.zbir, 2)}</span> RSD
      </p>

      <LoadingOverlay loading={loading}>
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          {rows.map((r, i) => (
            <RecordRow key={`${getId(r)}-${i}`} row={r} getId={getId} idLabel={idLabel} detail={detail} />
          ))}
        </ul>
      </LoadingOverlay>

      {pages > 1 && (
        <nav aria-label="Stranice" className="flex items-center justify-between gap-3">
          <Button variant="secondary" size="sm" icon={IconChevronLeft} disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
            Prethodna
          </Button>
          <span className="text-xs tabular-nums text-slate-600 dark:text-slate-300">
            Strana {page} od {pages}
          </span>
          <Button variant="secondary" size="sm" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>
            Sledeća
            <IconChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </div>
  );
}

// One row of the list. With a "detail" config the row expands in place (no second window) and loads its details on first open.
export function RecordRow({ row: r, getId, idLabel, detail }) {
  const [expanded, setExpanded] = useState(false);
  const Head = detail ? "button" : "div";
  return (
    <li>
      <Head
        type={detail ? "button" : undefined}
        onClick={detail ? () => setExpanded((v) => !v) : undefined}
        aria-expanded={detail ? expanded : undefined}
        className={`block w-full px-3 py-2.5 text-left ${detail ? "cursor-pointer transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-primary dark:hover:bg-slate-800/60" : ""}`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate text-sm font-medium tabular-nums text-slate-900 dark:text-slate-50">
            <span className="sr-only">{idLabel}: </span>
            {getId(r)}
          </span>
          <span className="flex shrink-0 items-baseline gap-2">
            <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-50">
              {formatNumber(r.vrednost, 2)} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">RSD</span>
            </span>
            {detail && (
              <IconChevronDown
                aria-hidden="true"
                className={`self-center text-sm text-slate-500 transition-transform dark:text-slate-400 ${expanded ? "rotate-180" : ""}`}
              />
            )}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
          <span className="tabular-nums">{formatDateTime(r.vreme)}</span>
          <span>Sto: {r.sto || "Neraspoređeno"}</span>
          <span>Konobar: {r.konobar || "-"}</span>
        </div>
      </Head>
      {detail && expanded && <RecordDetail row={r} detail={detail} />}
    </li>
  );
}

function RecordDetail({ row, detail }) {
  // detail.local: everything needed is already in the row (no extra request)
  if (detail.local) {
    return <div className="border-t border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-800 dark:bg-slate-800/40">{detail.render(row)}</div>;
  }
  return <RemoteRecordDetail row={row} detail={detail} />;
}

function RemoteRecordDetail({ row, detail }) {
  const [retryTick, setRetryTick] = useState(0);
  const { data, loading, error } = useApiData(`${detail.endpoint}?${detail.getQuery(row)}`, retryTick);
  const failed = Boolean(error) || (data && data.success === false);
  return (
    <div className="border-t border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-800 dark:bg-slate-800/40">
      {failed ? (
        <ErrorState message={data?.error} onRetry={() => setRetryTick((t) => t + 1)} />
      ) : loading || !data ? (
        <ListSkeleton rows={3} />
      ) : (
        detail.render(data)
      )}
    </div>
  );
}
